import type { CalendarProps, Days, Months } from './calendar.type';
import { useRef, useState, useCallback } from 'react';
import clsx from 'clsx';
import Control from '@/components/control';
import InputMasked from '@/components/input-masked';
import Button from '@/components/calendar/components/button';
import Modal from '@/components/calendar/components/modal';
import { CalendarProvider } from '@/components/calendar/context';
import { WEEK_DAY } from '@/components/calendar/utils';
import { useAppSelector } from '@/redux/store';
import { ConfigSlice } from '@/redux/config';
import Converter from '@/utils/converter';
import '@/components/calendar/calendar-v1.css';
import '@/components/calendar/calendar-v2.css';

export default function Calendar(props: CalendarProps) {
  const {
    name,
    label = '',
    mode = 'text',
    value = '',
    startPosition = new Date(),
    precision = 'day',
    period = ['no limit', 'no limit'],
    closeButton = 'Schließen',
    modalPosition = 'static',
    onChange,
    onFocus = () => {},
    onBlur = () => {},
    onOpen = () => {},
    onClose = () => {},
  } = props;
  const cssVersion = useAppSelector(ConfigSlice.selectors.cssVersion);
  const DAYS: Days =
    cssVersion === 1 ? ['M', 'D', 'M', 'D', 'F', 'S', 'S'] : ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
  const MONTHS: Months = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];
  const WEEK_START: WEEK_DAY = WEEK_DAY.MONDAY;
  const MASK = Converter.mask(precision);
  const TILL = 768;
  const formattedValue = precision === 'day' ? value : Converter.DateSting.ToMonthStyle(value);
  const [focused, setFocused] = useState<boolean>(false);
  const inputBorderRef = useRef<HTMLDivElement>(null);

  const onInputChange = (value: string, name: string): void => {
    // TODO: prevent onChange place
    onChange(precision === 'day' ? value : Converter.DateSting.ToDayStyle(value), name);
  };

  const onInputFocus = useCallback((): void => {
    setFocused(true);
    onFocus(name);
  }, [name, onFocus]);

  const onInputBlur = useCallback((): void => {
    setFocused(false);
    onBlur(name);
  }, [name, onBlur]);

  return (
    <CalendarProvider
      inputBorderRef={inputBorderRef}
      name={name}
      value={formattedValue}
      modalTill={TILL}
      precision={precision}
      period={period}
      startPosition={startPosition}
      dayNames={DAYS}
      monthNames={MONTHS}
      weekStart={WEEK_START}
      closeButton={closeButton}
      modalPosition={modalPosition}
      onDateChange={(value) => onChange(Converter.Date.ToDateString(value), name)}
      onOpenModal={(size) => onOpen(size, name)}
      onCloseModal={() => onClose(name)}
    >
      <div data-css-version={cssVersion} className='calendar'>
        <div className='calendar__input'>
          <div
            ref={inputBorderRef}
            className={clsx('calendar__input-border', {
              'calendar__input-border--focused': focused,
              'calendar__input-border--dropdown': mode === 'dropdown',
              'calendar__input-border--text': mode === 'text',
            })}
          >
            <div className='calendar__container'>
              <div className='calendar__content'>
                {cssVersion === 2 && <Control.Label postion='active'>{label}</Control.Label>}
                <div
                  className={clsx(
                    'calendar__inputs',
                    mode === 'dropdown' ? 'calendar__inputs--dropdown' : 'calendar__inputs--text',
                  )}
                >
                  {cssVersion === 2 && (
                    <input
                      type='text'
                      name={name}
                      defaultValue={formattedValue || MASK}
                      aria-hidden='true'
                      tabIndex={-1}
                      readOnly
                      className='calendar__input-field calendar__input-field--holder'
                    />
                  )}
                  <InputMasked
                    inputClassName='calendar__input-field'
                    placeholderClassName='calendar__input-placeholder'
                    name={name}
                    value={formattedValue}
                    mask={MASK}
                    onChange={onInputChange}
                    onFocus={onInputFocus}
                    onBlur={onInputBlur}
                  />
                </div>
              </div>
              {mode === 'dropdown' && <Button />}
            </div>
          </div>
          {mode === 'dropdown' && <Modal />}
        </div>
      </div>
    </CalendarProvider>
  );
}
